import { useTranslation } from 'react-i18next';

/**
 * Get the appropriate unit string (singular or plural) based on amount and language
 * @param unit - The unit type (0-15)
 * @param amount - The amount to determine if plural is needed
 * @param language - The current language ('en' or 'de')
 * @returns The localized unit string (singular or plural)
 */
export const getPluralizedIngredientUnit = (unit: number, amount: number, language: string = 'en'): string => {
  const isPlural = amount > 1;
  
  // Units that don't need pluralization (measurements, abbreviations)
  const noPluralUnits = [0, 1, 4, 5, 12]; // ml, g, EL, TL, leaves
  
  if (noPluralUnits.includes(unit) || !isPlural) {
    return getIngredientUnitString(unit);
  }
  
  // Get the plural form for units that need it
  switch (unit) {
    case 2: // UNIT
      return language === 'de' ? 'LABEL_UNIT' : 'LABEL_UNIT';
    case 3: // SLICE
      return language === 'de' ? 'LABEL_SLICE_PLURAL' : 'LABEL_SLICE_PLURAL';
    case 6: // CUP/SCOOP
      return language === 'de' ? 'LABEL_CUP_PLURAL' : 'LABEL_CUP_PLURAL';
    case 7: // PINCH
      return language === 'de' ? 'LABEL_PINCH_PLURAL' : 'LABEL_PINCH_PLURAL';
    case 8: // CLOVE
      return language === 'de' ? 'LABEL_CLOVE_PLURAL' : 'LABEL_CLOVE_PLURAL';
    case 9: // PKG
      return language === 'de' ? 'LABEL_PK_PLURAL' : 'LABEL_PK_PLURAL';
    case 10: // TUB
      return language === 'de' ? 'LABEL_TUB_PLURAL' : 'LABEL_TUB_PLURAL';
    case 11: // ROLL
      return language === 'de' ? 'LABEL_ROLL_PLURAL' : 'LABEL_ROLL_PLURAL';
    case 13: // BUNCH
      return language === 'de' ? 'LABEL_BUNCH_PLURAL' : 'LABEL_BUNCH_PLURAL';
    case 14: // HEAD
      return language === 'de' ? 'LABEL_HEAD_PLURAL' : 'LABEL_HEAD_PLURAL';
    case 15: // PORTION
      return language === 'de' ? 'LABEL_PORTION_UNIT_PLURAL' : 'LABEL_PORTION_UNIT_PLURAL';
    default:
      return getIngredientUnitString(unit);
  }
};

/**
 * Get the basic ingredient unit string (always singular)
 * @param unit - The unit type (0-15)
 * @returns The localized unit string
 */
export const getIngredientUnitString = (unit: number): string => {
  switch (unit) {
    case 0:
      return "LABEL_ML";
    case 1:
      return "LABEL_GR";
    case 2:
      return "LABEL_UNIT";
    case 3:
      return "LABEL_SLICE";
    case 4:
      return "LABEL_EL";
    case 5:
      return "LABEL_TL";
    case 6:
      return "LABEL_CUP";
    case 7:
      return "LABEL_PINCH";
    case 8:
      return "LABEL_CLOVE";
    case 9:
      return "LABEL_PK";
    case 10:
      return "LABEL_TUB";
    case 11:
      return "LABEL_ROLL";
    case 12:
      return "LABEL_LEAVES";
    case 13:
      return "LABEL_BUNCH";
    case 14:
      return "LABEL_HEAD";
    case 15:
      return "LABEL_PORTION_UNIT";
    default:
      return "-";
  }
};

/**
 * Hook to get pluralized ingredient unit with translation
 * @param unit - The unit type (0-15)
 * @param amount - The amount to determine if plural is needed
 * @returns The translated unit string (singular or plural)
 */
export const usePluralizedIngredientUnit = (unit: number, amount: number) => {
  const { t, i18n } = useTranslation();
  const {language} = i18n;
  
  const unitKey = getPluralizedIngredientUnit(unit, amount, language);
  return t(unitKey);
};
